import { Box, styled } from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useClient } from 'urql';

import { getConsultationType } from '@/assets/js/util';
import ConsultationDirectCancelPopupButton from '@/components/Consultations/ConsultationDirectCancelPopupButton';
import CalendarCancelPopupButton from '@/components/calcom/CalendarCancelPopupButton';
import CalendarPopupButton from '@/components/calcom/CalendarPopupButton';
import { FF_ENABLE_ALTERNATE_PENDING_CANCELLATION, FF_ENABLE_SHOPIFY_PAYMENT } from '@/constants/featureFlags';
import settings from '@/data/constants';
import { useFeatureFlags } from '@/hooks';
import { GET_PAYMENT_STATUS_QUERY } from '@/hooks/graphql/queries/usePaymentStatusQuery';
import type { Consultation } from '@/hooks/patients/usePatientConsultations';
import type { ConsultationSubStatusType } from '@/types';
import { buildCancellationLink, buildRescheduleLink } from '@/utils/calendar';
import { luxonFormatWithOrdinalSuffix } from '@/utils/luxon';
import { Link, toast } from '@montugroup/design-system';
import { DateTime } from 'luxon';
import type { ShowConsultation } from '../components/ConsultationTabSections/ShowConsultation';
import { getPaymentTypeText } from '../utils/getPaymentTypeText';

type UseConsultationTabOverviewTableProps = {
  patientEmail?: string | null;
  consultations: Consultation[];
  setShowConsultation: (showConsultation: Consultation) => void;
  setConsultationSubStatus?: (consultationSubStatus: ConsultationSubStatusType | null) => void;
  handleRefetchPatientData?: () => void;
  scheduledConsultId?: number;
  onViewClick?: (consultation: Consultation) => void;
};

export type ConsultationWithPaymentStatus = {
  consultationId: number;
  consultation_code: string;
  appointment_time: ReactNode;
  type: ReactNode;
  practitioner: string;
  payment_status: string | null;
  consultation_status: string;
  payment_type: string | false;
  view_link: ReactNode;
  reschedule_link: ReactNode | null;
  cancel_link: ReactNode | null;
};

type PaymentResponseStatus = {
  preAuthTransactionExists: boolean;
  debitTransactionExists: boolean;
  paymentStatus: string;
};

const StyledLink = styled(Link)({
  cursor: 'pointer',
});

const StyledAppointmentDate = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: '200px',
});

export const useConsultationTabOverviewTable = ({
  patientEmail,
  consultations,
  setShowConsultation,
  setConsultationSubStatus,
  handleRefetchPatientData,
  scheduledConsultId,
  onViewClick,
}: UseConsultationTabOverviewTableProps) => {
  const { flags } = useFeatureFlags();
  const ffAlterativePendingFlow = flags[FF_ENABLE_ALTERNATE_PENDING_CANCELLATION];
  const ffEnableShopifyPayment = flags[FF_ENABLE_SHOPIFY_PAYMENT];

  const [consultationsOverviewTableData, setConsultationsOverviewTableData] = useState<ConsultationWithPaymentStatus[]>(
    [],
  );

  const [isProcessingConsultations, setIsProcessingConsultations] = useState(false);

  const client = useClient();
  const shouldFetchPaymentStatus = Boolean(patientEmail?.trim());

  const formatConsultationAppointmentTime = (consultation: Consultation) => {
    const { start_time, end_time, ConsultationType } = consultation;
    const startTime = start_time ? DateTime.fromISO(start_time) : null;
    const endTime = end_time ? DateTime.fromISO(end_time) : null;

    const formattedConsultDate = startTime
      ? `${startTime.toFormat('MMMM')} ${luxonFormatWithOrdinalSuffix(startTime.day)} ${startTime.toFormat('yyyy')}`
      : '';

    const formattedStartTime = startTime?.toFormat('h:mm a').toLowerCase() || '';
    const formattedEndTime = endTime?.toFormat('h:mm a').toLowerCase() || '';
    const durationTime = ConsultationType?.duration_mins || 0;

    return (
      <StyledAppointmentDate>
        <span>{formattedConsultDate}</span>
        <span>{`${formattedStartTime} - ${formattedEndTime} (${durationTime} mins)`}</span>
      </StyledAppointmentDate>
    );
  };

  const formatPractitionerName = (consultation: Consultation) => {
    return consultation.Doctor
      ? `${consultation.Doctor?.DoctorUser?.first_name} ${consultation.Doctor?.DoctorUser?.last_name}`
      : `${consultation.Nurse?.NurseUser?.first_name} ${consultation.Nurse?.NurseUser?.last_name}`;
  };

  const formatConsultationType = (consultation: Consultation) => {
    return <Box minWidth={'80px'}>{getConsultationType(consultation)}</Box>;
  };

  const formatPaymentStatus = (consultation: Consultation, paymentStatus?: PaymentResponseStatus) => {
    let content;
    if (paymentStatus?.preAuthTransactionExists) {
      content = 'Credit Card';
    } else {
      content =
        paymentStatus?.paymentStatus === 'PAID'
          ? consultation.paid_date
            ? DateTime.fromISO(consultation.paid_date).toFormat('dd-MMM-yyyy')
            : ''
          : consultation.payment_status;
    }

    return content;
  };

  const formatConsultationStatus = (consultation: Consultation) => {
    if (consultation.id === scheduledConsultId) {
      return 'In Progress';
    }

    const status = consultation.ConsultationStatus?.name;
    const subStatus = consultation.ConsultationSubStatus?.name;
    return [status, subStatus ? `- ${subStatus}` : ''].filter(Boolean).join(' ');
  };

  const formatPaymentType = (consultation: Consultation) => {
    if (!ffEnableShopifyPayment && consultation.Doctor) {
      return '-';
    }

    return getPaymentTypeText({
      consultation: consultation as ShowConsultation,
      ffEnableShopifyPayment,
    });
  };

  const createViewLink = useCallback(
    (consultation: Consultation) => {
      return (
        <Box width="60px">
          <StyledLink
            component="button"
            onClick={
              consultation.id === scheduledConsultId
                ? undefined
                : () => {
                    setShowConsultation(consultation);
                    if (onViewClick) {
                      onViewClick(consultation);
                    }
                    if (
                      consultation.ConsultationSubStatus &&
                      consultation.ConsultationStatus?.name === 'Ineligible' &&
                      setConsultationSubStatus
                    ) {
                      setConsultationSubStatus(consultation.ConsultationSubStatus);
                    }
                  }
            }
            data-testid="view-consultation-details"
          >
            View
          </StyledLink>
        </Box>
      );
    },
    [setConsultationSubStatus, setShowConsultation, scheduledConsultId],
  );

  useEffect(() => {
    const createRescheduleLink = (consultation: Consultation): ReactNode | null => {
      // Only needed if the reschedule column is displayed in the overview table.
      // Not shown in GDC consult history, as rescheduling is not supported in that view
      if (!consultation.calcom_consultation_uuid || !handleRefetchPatientData) {
        return null;
      }

      const isScheduledOrPending =
        consultation.status_id === settings.consultationStatus.scheduled ||
        consultation.status_id === settings.consultationStatus.pending;
      const rescheduleLink = buildRescheduleLink(consultation.calcom_consultation_uuid);
      const showRescheduleButton = isScheduledOrPending && rescheduleLink;

      return (
        showRescheduleButton && (
          <CalendarPopupButton
            variant="text"
            link={rescheduleLink}
            onRescheduleBookingSuccessful={handleRefetchPatientData}
          >
            Reschedule
          </CalendarPopupButton>
        )
      );
    };

    const createCancelLink = (consultation: Consultation, alternativePending: boolean): ReactNode | null => {
      // Only needed if the cancel column is displayed in the overview table.
      // Not shown in GDC consult history, as cancellation is not supported in that view
      if (!consultation.calcom_consultation_uuid || !handleRefetchPatientData) {
        return null;
      }

      const isScheduled = consultation.status_id === settings.consultationStatus.scheduled;
      const isPending = consultation.status_id === settings.consultationStatus.pending;
      const isScheduledOrPending = isScheduled || isPending;
      const cancelBookingUrl = buildCancellationLink(consultation.calcom_consultation_uuid);
      const showCancelButton = isScheduledOrPending && cancelBookingUrl;

      return (
        showCancelButton &&
        ((isScheduled && (
          <CalendarCancelPopupButton cancelBookingUrl={cancelBookingUrl} onClose={handleRefetchPatientData} />
        )) ||
          (isPending && alternativePending && (
            <ConsultationDirectCancelPopupButton consultationId={consultation.id} onClose={handleRefetchPatientData} />
          )))
      );
    };

    const fetchPaymentStatus = async (consultation: Consultation) => {
      // Only make this request if we have the patient email and consultation.calcom_consultation_uuid.
      // Within GDC we aren't displaying any payment details so we ommit patient email
      if (!patientEmail || patientEmail.trim() === '' || !consultation.calcom_consultation_uuid) {
        return undefined;
      }

      try {
        const paymentStatusResponse = await client
          .query(GET_PAYMENT_STATUS_QUERY, {
            getPaymentStatusInput: {
              email: patientEmail,
              merchantTransactionId: consultation.calcom_consultation_uuid as string,
            },
          })
          .toPromise();

        return paymentStatusResponse?.data?.getPaymentStatus as PaymentResponseStatus;
      } catch {
        return undefined;
      }
    };

    const createConsultationRow = (consultation: Consultation, paymentStatus?: PaymentResponseStatus) => {
      return {
        consultationId: consultation.id,
        consultation_code: consultation.consultation_code,
        appointment_time: formatConsultationAppointmentTime(consultation),
        type: formatConsultationType(consultation),
        practitioner: formatPractitionerName(consultation),
        payment_status: formatPaymentStatus(consultation, paymentStatus),
        consultation_status: formatConsultationStatus(consultation),
        payment_type: formatPaymentType(consultation),
        view_link: createViewLink(consultation),
        reschedule_link: createRescheduleLink(consultation),
        cancel_link: createCancelLink(consultation, ffAlterativePendingFlow),
      };
    };

    const appendConsultationsWithPaymentStatus = async () => {
      try {
        setIsProcessingConsultations(true);

        if (!shouldFetchPaymentStatus) {
          // No payment data needed so just return the table data
          const consultationsData = consultations.map((consultation) => createConsultationRow(consultation));
          setConsultationsOverviewTableData(consultationsData);
          return;
        }

        const consultationsWithPaymentStatus = await Promise.all(
          consultations.map(async (consultation) => {
            const paymentStatus = await fetchPaymentStatus(consultation);
            return createConsultationRow(consultation, paymentStatus);
          }),
        );

        setConsultationsOverviewTableData(consultationsWithPaymentStatus);
      } catch {
        toast.error('Error while fetching payment status data for consultations');
      } finally {
        setIsProcessingConsultations(false);
      }
    };

    appendConsultationsWithPaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, consultations, createViewLink, ffAlterativePendingFlow, patientEmail, handleRefetchPatientData]);

  return {
    consultationsOverviewTableData,
    isProcessingConsultations,
  };
};
